/**
 * @author: lv.chen
 * @date: 2025/4/1
 * @desc: Pipeline/Change Log页面Change Checklist页签 筛选器组件
 */
import type { IDynamicFilterControl } from '@gp/components/DynamicFilter/components/FilterControl/types';
import type { IDynamicFilterField } from '@gp/components/DynamicFilter/types';
import FilterCommon from '@gp/components/FilterCommon';
import RangePickerButton from '@gp/components/RangePickerButton';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import { COMMON_SELECT_MULTIPLE_CONTROL_CFG } from '@gp/constants/select';
import type {
  IPipelineChangeChecklistFilterConditionsVO,
  IPipelineChangeChecklistFilterVO,
} from '@gp/types/changeLog';
import type { IRecord } from '@gp/types/global';
import { getDisabledDateByDateType } from '@gp/utils/date';
import type { IBaseProps } from '@home/types/common';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { CHANGE_CHECKLIST_DATE_TYPES } from '../../constants';

interface IChangeChecklistFilterProps extends IBaseProps {
  loading?: boolean;
  data?: IPipelineChangeChecklistFilterConditionsVO | null;
  value?: IPipelineChangeChecklistFilterVO;
  onChange?: (payload: IPipelineChangeChecklistFilterVO) => void;
}

const DEFAULT_CONTROLS: IRecord<IDynamicFilterControl> = {
  rangePicker: {
    showSelectType: false,
    showShortcut: true,
    typeList: CHANGE_CHECKLIST_DATE_TYPES,
  },
  priority: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  pteam: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    listWidth: 260,
    options: [],
  },
  stage: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
};

const ChangeChecklistFilter: React.FC<IChangeChecklistFilterProps> = (
  props,
) => {
  const {
    style,
    className,
    loading = true,
    data,
    value: valueProps,
    onChange: onChangeProps,
    allFilterOptionsValues,
    onReset,
    scrollBody,
    headerTitle,
    currentPage,
    updateDeps,
    handleSearch,
    getSearchList,
    formatSearchResults,
    searchVal,
    searchList,
    showRightBtn,
    selectedValue,
    setSelectedValue,
    setSearchList,
    setSearchVal,
  } = props;

  const controls = useMemo<IRecord<IDynamicFilterControl>>(() => {
    if (loading) return DEFAULT_CONTROLS;
    const {
      rangePicker: rangePickerOptions,
      priority: priorityOptions,
      pteam: pteamOptions,
      stage: stageOptions,
      project_id: cascadeProjectOptions,
    } = data || {};
    const { minDate, maxDate } = rangePickerOptions || {};
    const controlsTmp = cloneDeep(DEFAULT_CONTROLS);

    return merge(controlsTmp, {
      rangePicker: {
        startOfTime: minDate,
        endOfTime: maxDate,
        disabledDate: {
          date: getDisabledDateByDateType('date', minDate, maxDate),
          week: getDisabledDateByDateType('week', minDate, maxDate),
          month: getDisabledDateByDateType('month', minDate, maxDate),
        },
        disabledMonth: getDisabledDateByDateType('month', minDate, maxDate),
      },
      priority: {
        options: priorityOptions,
      },
      // pteam: {
      //   options: pteamOptions,
      // },
      project_id: {
        options: cascadeProjectOptions,
        allValues: allFilterOptionsValues?.project_id || [],
        btnTitle: 'P-team/BO/Studio/Project',
      },
      stage: {
        options: stageOptions,
      },
    });
  }, [loading, data]);

  const [value, setValue] = useState<IPipelineChangeChecklistFilterVO>({
    rangePicker: {
      dateType: '',
      startDate: '',
      endDate: '',
    },
    priority: [],
    // pteam: [],
    project_id: [],
    stage: [],
  });

  const fields = useMemo<IDynamicFilterField[]>(() => {
    const { rangePicker } = value || {};
    const { project_id: projectOptions } = data || {};
    const projectDisplay = loading
      ? false
      : Array.isArray(projectOptions) && projectOptions.length > 0;
    return [
      {
        key: 'rangePicker',
        type: 'rangePicker',
        label: (
          <RangePickerButton.CommonDateRangePickerButton value={rangePicker} />
        ),
        saveKey: 'checklist_rangePicker',
      },
      {
        key: KEYS_MAPPING.CASCADE_PROJECT,
        type: 'cascadeProject',
        display: projectDisplay,
        label: NAMES_MAPPING.CASCADE_PROJECT,
      },
      { key: 'stage', type: 'selectMultiple', label: 'Stage' },
    ];
  }, [value, data]);

  const handleChange = (payload: IRecord<any>) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
  };

  const handleConfirm = () => {
    onChangeProps?.(value);
  };

  useEffect(() => {
    if (!isEqual(value, valueProps)) {
      setValue({ ...value, ...valueProps });
    }
  }, [valueProps]);

  return (
    <FilterCommon
      style={style}
      className={className}
      controls={controls}
      value={value}
      onChange={handleChange}
      isNeedDiff={true}
      fields={fields}
      confirm={handleConfirm}
      onReset={onReset}
      loading={loading}
      scrollBody={scrollBody}
      filterSearchHeight={130}
      filterHeight={60}
      headerTitle={headerTitle}
      showRightBtn={showRightBtn}
      currentPage={currentPage}
      formatSearchResults={formatSearchResults}
      updateDeps={updateDeps}
      searchVal={searchVal}
      searchList={searchList}
      handleSearch={handleSearch}
      getSearchList={getSearchList}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      setSearchList={setSearchList}
      setSearchVal={setSearchVal}
    />
  );
};

export default ChangeChecklistFilter;
